import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SchemeWiseBroker } from '@/types/client';
import { formatDate, formatCurrency, formatNumber, formatSummaryAmount } from '@/utils/formatters';
import { useAutoHideMessage } from '@/hooks/useAutoHideMessage';
import { useIsMounted } from '@/hooks/useIsMounted';
import OrderSectionHeader from './OrderSectionHeader';
import TableWrapper from './TableWrapper';
import styles from './OrderManagement.module.css';
import { AggregatedOrder, CalculatedOrder, CalculatedOrdersSummary, QuantityUpdate } from './types';
import ClientWiseOrdersSection from './ClientWiseOrdersSection';
import SubmitOrdersModal from './SubmitOrdersModal';
import shared from '@/components/utils/shared.module.css';
import { Button, Note, Placeholder } from '@/components/ui';
import OrderStatusPill, { calculatedOrderStage } from './orderStatus';

export type OrderSide = 'buy' | 'sell';

export interface EtfClientOrder {
    id: number;
    order_date: string;
    scheme: { id: number; code: string; currency?: string; order_calc_type?: number } | null;
    company: { id: number; symbol: string } | null;
    client: { id: number; code: string; name?: string } | null;
    broker: { id: number; code: string } | null;
    quantity: number | null;
    amount: string | null;
    cash_env?: number;
    cash_env_display?: string;
    limit_price?: string | null;
    order_status: number;
    order_status_display: string;
}

export interface CalcFetchResult {
    aggregatedOrders: AggregatedOrder[];
    nriOrders?: CalculatedOrder[];         // equity only
    clientOrders?: EtfClientOrder[];       // etf only, for client-wise editing
    summary?: CalculatedOrdersSummary;     // equity only
}

export interface BaseCalculatedOrdersProps {
    titlePrefix?: string;
    supportsNri?: boolean;
    supportsClientWiseEdit?: boolean;
    onOrdersSubmitted: () => void;
    
    fetchBuyOrders: () => Promise<CalcFetchResult>;
    fetchSellOrders: () => Promise<CalcFetchResult>;
    
    bulkAssignBroker: (side: OrderSide, orderIds: number[], brokerId: number | null) => Promise<void>;
    fetchBrokersByScheme: (schemeId: number, clientId?: number) => Promise<SchemeWiseBroker[]>;
    
    submitOrders: (side: OrderSide, orderIds: number[], emailRecipients?: string[]) => Promise<{
        orders_submitted: number;
        submission_id?: string;
        email_recipients?: string[];
    }>;
    
    updateClientOrderQuantities?: (side: OrderSide, updates: QuantityUpdate[]) => Promise<void>;
    
    renderExtraAggregatedHeaders?: () => React.ReactNode;
    renderExtraAggregatedCells?: (order: AggregatedOrder) => React.ReactNode;
    
    getAmountLabel?: (side: OrderSide) => string;
}

const BaseCalculatedOrders: React.FC<BaseCalculatedOrdersProps> = ({
    titlePrefix = '',
    supportsNri = false,
    supportsClientWiseEdit = false,
    onOrdersSubmitted,
    fetchBuyOrders,
    fetchSellOrders,
    bulkAssignBroker,
    fetchBrokersByScheme,
    submitOrders,
    updateClientOrderQuantities,
    renderExtraAggregatedHeaders,
    renderExtraAggregatedCells,
    getAmountLabel = () => 'Total Amount'
}) => {
    // Orders state
    const [aggregatedBuyOrders, setAggregatedBuyOrders] = useState<AggregatedOrder[]>([]);
    const [aggregatedSellOrders, setAggregatedSellOrders] = useState<AggregatedOrder[]>([]);
    const [nriBuyOrders, setNriBuyOrders] = useState<CalculatedOrder[]>([]);
    const [nriSellOrders, setNriSellOrders] = useState<CalculatedOrder[]>([]);
    const [clientBuyOrders, setClientBuyOrders] = useState<EtfClientOrder[]>([]);
    const [clientSellOrders, setClientSellOrders] = useState<EtfClientOrder[]>([]);
    const [buySummary, setBuySummary] = useState<CalculatedOrdersSummary | null>(null);
    const [sellSummary, setSellSummary] = useState<CalculatedOrdersSummary | null>(null);

    // Broker fetching
    const [brokersByScheme, setBrokersByScheme] = useState<Map<string, SchemeWiseBroker[]>>(new Map());
    const fetchedSchemeIdsRef = useRef<Set<string>>(new Set());

    // Selections - Aggregated
    const [selectedBuyAggregated, setSelectedBuyAggregated] = useState<Set<string>>(new Set());
    const [selectedSellAggregated, setSelectedSellAggregated] = useState<Set<string>>(new Set());
    const [pendingBuyAggregatedBrokers, setPendingBuyAggregatedBrokers] = useState<Map<string, number | null>>(new Map());
    const [pendingSellAggregatedBrokers, setPendingSellAggregatedBrokers] = useState<Map<string, number | null>>(new Map());

    // Selections - NRI
    const [selectedBuyNri, setSelectedBuyNri] = useState<Set<number>>(new Set());
    const [selectedSellNri, setSelectedSellNri] = useState<Set<number>>(new Set());
    const [pendingBuyNriBrokers, setPendingBuyNriBrokers] = useState<Map<number, number | null>>(new Map());
    const [pendingSellNriBrokers, setPendingSellNriBrokers] = useState<Map<number, number | null>>(new Map());

    // Action states
    const [assigningBrokers, setAssigningBrokers] = useState<OrderSide | null>(null);
    // Scheme id → broker picked in the Non-NRI bulk bar; absent means the scheme default.
    const [bulkBuyBrokers, setBulkBuyBrokers] = useState<Map<number, number>>(new Map());
    const [bulkSellBrokers, setBulkSellBrokers] = useState<Map<number, number>>(new Map());
    const [submitting, setSubmitting] = useState<OrderSide | null>(null);
    const [pendingSubmit, setPendingSubmit] = useState<OrderSide | null>(null);
    const [emailRecipients, setEmailRecipients] = useState('');
    
    // Status states
    // Only the initial load shows the spinner; refreshes after actions keep the tables visible.
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const isMounted = useIsMounted();

    useAutoHideMessage(successMessage, () => setSuccessMessage(null));

    // The wrappers pass the fetchers as inline arrows, so their identity changes on
    // every parent render. Read them through a ref — depending on them directly
    // refetched both sides each time the parent re-rendered.
    const fetchersRef = useRef({ fetchBuyOrders, fetchSellOrders });
    fetchersRef.current = { fetchBuyOrders, fetchSellOrders };

    // Resolves to whether the orders were reloaded; a failure is reported through the page-level error.
    const loadOrders = useCallback(async (): Promise<boolean> => {
        try {
            const { fetchBuyOrders: fetchBuy, fetchSellOrders: fetchSell } = fetchersRef.current;
            const [buyRes, sellRes] = await Promise.all([fetchBuy(), fetchSell()]);
            if (!isMounted.current) return false;
            
            setAggregatedBuyOrders(buyRes.aggregatedOrders || []);
            setAggregatedSellOrders(sellRes.aggregatedOrders || []);
            
            setNriBuyOrders(buyRes.nriOrders || []);
            setNriSellOrders(sellRes.nriOrders || []);
            
            setClientBuyOrders(buyRes.clientOrders || []);
            setClientSellOrders(sellRes.clientOrders || []);
            
            setBuySummary(buyRes.summary || null);
            setSellSummary(sellRes.summary || null);
            return true;
        } catch {
            if (isMounted.current) setError(`Failed to load calculated ${titlePrefix} orders`);
            return false;
        } finally {
            if (isMounted.current) setInitialLoading(false);
        }
    }, [titlePrefix, isMounted]);

    useEffect(() => { loadOrders(); }, [loadOrders]);

    // Reload after saving so the client rows and the aggregated totals show the new quantities.
    // Rejects if the save fails; resolves to false if the save went through but the reload did not.
    const updateQuantitiesAndReload = useCallback(async (side: OrderSide, updates: QuantityUpdate[]) => {
        if (!updateClientOrderQuantities) return true;
        await updateClientOrderQuantities(side, updates);
        return loadOrders();
    }, [updateClientOrderQuantities, loadOrders]);

    useEffect(() => {
        let cancelled = false;

        const fetchBrokers = async () => {
            const keysToFetch = new Map<string, { schemeId: number; clientId?: number }>();

            const addKey = (schemeId: number, clientId?: number) => {
                const key = clientId ? `scheme_${schemeId}_client_${clientId}` : `scheme_${schemeId}`;
                if (!fetchedSchemeIdsRef.current.has(key) && !keysToFetch.has(key)) {
                    keysToFetch.set(key, { schemeId, clientId });
                }
            };

            [...aggregatedBuyOrders, ...aggregatedSellOrders].forEach(o => addKey(o.scheme.id));
            if (supportsNri) {
                [...nriBuyOrders, ...nriSellOrders].forEach(o => addKey(o.scheme.id, o.client?.id));
            }

            if (keysToFetch.size === 0) return;

            const entries = Array.from(keysToFetch.entries());

            try {
                const results = await Promise.all(
                    entries.map(([, { schemeId, clientId }]) =>
                        fetchBrokersByScheme(schemeId, clientId).catch(() => [] as SchemeWiseBroker[])
                    )
                );

                if (cancelled) return;

                entries.forEach(([key]) => fetchedSchemeIdsRef.current.add(key));

                setBrokersByScheme(prev => {
                    const next = new Map(prev);
                    entries.forEach(([key], i) => next.set(key, results[i]));
                    return next;
                });
            } catch (e) {
                // Ignore fetch broker errors
            }
        };

        fetchBrokers();
        return () => { cancelled = true; };
    }, [aggregatedBuyOrders, aggregatedSellOrders, nriBuyOrders, nriSellOrders, supportsNri, fetchBrokersByScheme]);

    const getDefaultBroker = (order: CalculatedOrder): SchemeWiseBroker | null => {
        const key = order.client?.is_nri ? `scheme_${order.scheme.id}_client_${order.client.id}` : `scheme_${order.scheme.id}`;
        const brokers = brokersByScheme.get(key);
        return brokers?.find(b => b.is_default) ?? null;
    };

    const aggregatedKey = (order: AggregatedOrder) => `${order.scheme.id}-${order.company.id}`;

    // Brokers a whole group can share — an NRI code belongs to one client only.
    const sharedSchemeBrokers = (schemeId: number) =>
        (brokersByScheme.get(`scheme_${schemeId}`) ?? []).filter(b => !b.nri_client_master);

    const getBulkBroker = (side: OrderSide, schemeId: number): number | undefined => {
        const chosen = (side === 'buy' ? bulkBuyBrokers : bulkSellBrokers).get(schemeId);
        if (chosen !== undefined) return chosen;
        const brokers = sharedSchemeBrokers(schemeId);
        return (brokers.find(b => b.is_default) ?? brokers[0])?.id;
    };

    const handleBulkBrokerChange = (side: OrderSide, schemeId: number, brokerId: number) => {
        // The empty "No brokers" placeholder parses to NaN — never store it as a pick.
        if (Number.isNaN(brokerId)) return;
        const setBulk = side === 'buy' ? setBulkBuyBrokers : setBulkSellBrokers;
        setBulk(prev => new Map(prev).set(schemeId, brokerId));
    };

    // Fills the bulk broker into every selected group as a pending change;
    // "Assign Brokers" still saves it. NRI orders keep their own client's broker.
    const handleApplyBulkBroker = (side: OrderSide) => {
        const aggOrders = side === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
        const selAgg = side === 'buy' ? selectedBuyAggregated : selectedSellAggregated;
        const setPending = side === 'buy' ? setPendingBuyAggregatedBrokers : setPendingSellAggregatedBrokers;
        setPending(prev => {
            const next = new Map(prev);
            aggOrders.forEach(order => {
                const key = aggregatedKey(order);
                const brokerId = getBulkBroker(side, order.scheme.id);
                if (selAgg.has(key) && brokerId !== undefined) next.set(key, brokerId);
            });
            return next;
        });
    };

    // --- Aggregated Handlers ---
    const handleAggregatedSelect = (key: string, side: OrderSide) => {
        const sel = side === 'buy' ? new Set(selectedBuyAggregated) : new Set(selectedSellAggregated);
        sel.has(key) ? sel.delete(key) : sel.add(key);
        side === 'buy' ? setSelectedBuyAggregated(sel) : setSelectedSellAggregated(sel);
    };

    const handleSelectAllAggregated = (side: OrderSide, checked: boolean) => {
        const orders = side === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
        const set = side === 'buy' ? setSelectedBuyAggregated : setSelectedSellAggregated;
        if (checked) {
            set(new Set(orders.map(aggregatedKey)));
        } else {
            set(new Set());
        }
    };

    const handleAggregatedBrokerChange = (key: string, side: OrderSide, brokerId: number | null) => {
        const setPending = side === 'buy' ? setPendingBuyAggregatedBrokers : setPendingSellAggregatedBrokers;
        setPending(prev => new Map(prev).set(key, brokerId));
        
        const setSel = side === 'buy' ? setSelectedBuyAggregated : setSelectedSellAggregated;
        setSel(prev => new Set(prev).add(key));
    };

    // --- NRI Handlers ---
    const handleNriSelect = (id: number, side: OrderSide) => {
        const sel = side === 'buy' ? new Set(selectedBuyNri) : new Set(selectedSellNri);
        sel.has(id) ? sel.delete(id) : sel.add(id);
        side === 'buy' ? setSelectedBuyNri(sel) : setSelectedSellNri(sel);
    };

    const handleSelectAllNri = (side: OrderSide, checked: boolean) => {
        const orders = side === 'buy' ? nriBuyOrders : nriSellOrders;
        const set = side === 'buy' ? setSelectedBuyNri : setSelectedSellNri;
        if (checked) {
            set(new Set(orders.map(o => o.id)));
        } else {
            set(new Set());
        }
    };

    const handleNriBrokerChange = (id: number, side: OrderSide, brokerId: number | null) => {
        const setPending = side === 'buy' ? setPendingBuyNriBrokers : setPendingSellNriBrokers;
        setPending(prev => new Map(prev).set(id, brokerId));
        
        const setSel = side === 'buy' ? setSelectedBuyNri : setSelectedSellNri;
        setSel(prev => new Set(prev).add(id));
    };

    // --- Assign Brokers ---
    const handleAssignBrokers = async (side: OrderSide) => {
        const selAgg = side === 'buy' ? selectedBuyAggregated : selectedSellAggregated;
        const selNri = side === 'buy' ? selectedBuyNri : selectedSellNri;
        
        if (selAgg.size === 0 && selNri.size === 0) {
            setError('Please select at least one item'); 
            return; 
        }

        try {
            setAssigningBrokers(side);
            setError(null);

            const aggOrders = side === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
            const pendingAgg = side === 'buy' ? pendingBuyAggregatedBrokers : pendingSellAggregatedBrokers;
            
            const nriOrders = side === 'buy' ? nriBuyOrders : nriSellOrders;
            const pendingNri = side === 'buy' ? pendingBuyNriBrokers : pendingSellNriBrokers;

            // One request per scheme and broker, not per group: the endpoint takes many orders
            // of one scheme, and a request per group sent hundreds at once, more than the
            // API's instances could take, so Cloud Run rejected the rest with 429s.
            const batches = new Map<string, { brokerId: number | null; orderIds: number[] }>();
            const addToBatch = (schemeId: number, brokerId: number | null, orderIds: number[]) => {
                const key = `${schemeId}:${brokerId}`;
                const batch = batches.get(key) ?? { brokerId, orderIds: [] };
                batch.orderIds.push(...orderIds);
                batches.set(key, batch);
            };

            selAgg.forEach(key => {
                const order = aggOrders.find(o => `${o.scheme.id}-${o.company.id}` === key);
                if (order) {
                    const pending = pendingAgg.get(key);
                    const brokerId = pending !== undefined ? pending : (order.broker?.id ?? null);
                    addToBatch(order.scheme.id, brokerId, order.order_ids);
                }
            });

            if (supportsNri) {
                selNri.forEach(id => {
                    const order = nriOrders.find(o => o.id === id);
                    if (order) {
                        let brokerId: number | null | undefined = pendingNri.get(id);
                        if (brokerId === undefined) brokerId = order.broker?.id;
                        if (brokerId === undefined) brokerId = getDefaultBroker(order)?.id;
                        if (brokerId === undefined) brokerId = null;

                        addToBatch(order.scheme.id, brokerId, [id]);
                    }
                });
            }

            await Promise.all([...batches.values()].map(batch =>
                bulkAssignBroker(side, batch.orderIds, batch.brokerId)));
            
            setSuccessMessage(`Brokers assigned to ${selAgg.size + selNri.size} ${titlePrefix} ${side} order group(s)`);
            
            if (side === 'buy') {
                setSelectedBuyAggregated(new Set());
                setSelectedBuyNri(new Set());
                setPendingBuyAggregatedBrokers(new Map());
                setPendingBuyNriBrokers(new Map());
            } else {
                setSelectedSellAggregated(new Set());
                setSelectedSellNri(new Set());
                setPendingSellAggregatedBrokers(new Map());
                setPendingSellNriBrokers(new Map());
            }
            
            await loadOrders();
        } catch (err: unknown) {
            const raw = err as Record<string, unknown>;
            const errData = ((raw.response as { data?: Record<string, unknown> })?.data ?? raw) as {
                error?: string; details?: string;
            };
            setError(errData.error || errData.details || `Failed to assign brokers to ${side} orders`);
        } finally {
            setAssigningBrokers(null);
        }
    };

    // --- Submission ---
    const confirmSubmit = async () => {
        if (!pendingSubmit) return;
        const side = pendingSubmit;
        
        const aggOrders = side === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
        const nriOrders = side === 'buy' ? nriBuyOrders : nriSellOrders;
        
        try {
            setSubmitting(side);
            setError(null);
            setPendingSubmit(null);
            
            const recipients = emailRecipients.split(',').map(e => e.trim()).filter(Boolean);
            
            let order_ids = aggOrders.flatMap(o => o.order_ids);
            if (supportsNri) {
                order_ids = [...order_ids, ...nriOrders.map(o => o.id)];
            }
            
            const result = await submitOrders(side, order_ids, recipients.length > 0 ? recipients : undefined);
            
            setSuccessMessage(
                `Submitted ${result.orders_submitted} ${titlePrefix} ${side} orders.` +
                (result.submission_id ? ` ID: ${result.submission_id}.` : '') +
                (result.email_recipients?.length ? ` Email: ${result.email_recipients.join(', ')}` : '')
            );
            
            setEmailRecipients('');
            
            if (side === 'buy') {
                setSelectedBuyNri(new Set());
            } else {
                setSelectedSellNri(new Set());
            }

            await loadOrders();
            onOrdersSubmitted();
        } catch (err: unknown) {
            const raw = err as Record<string, unknown>;
            // api.ts re-throws error.response.data directly, so err may be the raw data object
            const errData = ((raw.response as { data?: Record<string, unknown> })?.data ?? raw) as Record<string, unknown>;
            // Django ValidationError wraps dict values in arrays; unwrap if needed
            const getStr = (v: unknown) => (Array.isArray(v) ? v[0] : v) as string | undefined;
            const action = getStr(errData.action);
            const errorMsg = getStr(errData.error);
            const message = getStr(errData.message);
            const detail = getStr(errData.detail);
            if (action === 'reauthorize') {
                setError(`${errorMsg} — ${message}`);
            } else {
                setError(errorMsg || detail || `Failed to submit ${titlePrefix} ${side} orders`);
            }
        } finally {
            setSubmitting(null);
        }
    };

    // --- Rendering Helpers ---
    const renderSchemeSection = (side: OrderSide) => {
        const label = side === 'buy' ? 'Buy' : 'Sell';
        const aggOrders = side === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
        const nriOrders = side === 'buy' ? nriBuyOrders : nriSellOrders;
        const summary = side === 'buy' ? buySummary : sellSummary;
        
        const selAgg = side === 'buy' ? selectedBuyAggregated : selectedSellAggregated;
        const selNri = side === 'buy' ? selectedBuyNri : selectedSellNri;
        
        const pendingAgg = side === 'buy' ? pendingBuyAggregatedBrokers : pendingSellAggregatedBrokers;
        const pendingNri = side === 'buy' ? pendingBuyNriBrokers : pendingSellNriBrokers;
        
        const isAggAll = aggOrders.length > 0 && selAgg.size === aggOrders.length;
        const isNriAll = nriOrders.length > 0 && selNri.size === nriOrders.length;
        
        const assigning = assigningBrokers === side;
        const isSubmitting = submitting === side;
        
        const amountLabel = getAmountLabel(side);
        const prefixStr = titlePrefix ? `${titlePrefix} ` : '';
        const titleStr = titlePrefix ? `Scheme-wise ${titlePrefix} ${label} Orders` : `Calculated ${label} Orders`;
        
        const isEmpty = aggOrders.length === 0 && (!supportsNri || nriOrders.length === 0);
        const selectedSchemes = [
            ...new Map(aggOrders.filter(o => selAgg.has(aggregatedKey(o))).map(o => [o.scheme.id, o.scheme])).values(),
        ];

        return (
            <div className={styles['order-section']}>
                <OrderSectionHeader
                    side={side}
                    title={titleStr}
                    right={
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setPendingSubmit(side)}
                            disabled={isSubmitting || isEmpty}
                        >
                            {isSubmitting ? 'Submitting…' : `Submit All ${label} Orders`}
                        </Button>
                    }
                />

                {summary && (
                    <div className={styles['summary-row']}>
                        <div className={styles['summary-chip']}>
                            <span className={styles['summary-chip-label']}>Total Orders</span>
                            <span className={styles['summary-chip-value']}>{formatNumber(summary.total_count, 0)}</span>
                        </div>
                        <div className={styles['summary-chip']}>
                            <span className={styles['summary-chip-label']}>Total Quantity</span>
                            <span className={styles['summary-chip-value']}>{formatNumber(summary.total_quantity, 0)}</span>
                        </div>
                        <div className={styles['summary-chip']}>
                            <span className={styles['summary-chip-label']}>Total Amount</span>
                            <span className={styles['summary-chip-value']}>
                                {formatSummaryAmount(summary.total_amount, summary.currency)}
                            </span>
                        </div>
                    </div>
                )}

                {isEmpty ? (
                    <div className={styles.empty}>
                        <Placeholder message={<>No calculated {prefixStr.toLowerCase()}{label.toLowerCase()} orders found.</>} />
                    </div>
                ) : (
                    <>
                        <div className={styles.toolbar}>
                            <span className={styles['toolbar-info']}>
                                {supportsNri 
                                    ? `${selAgg.size} group${selAgg.size !== 1 ? 's' : ''} + ${selNri.size} NRI order${selNri.size !== 1 ? 's' : ''} selected`
                                    : `${selAgg.size} of ${aggOrders.length} scheme${aggOrders.length !== 1 ? 's' : ''} selected`
                                }
                            </span>
                            <div className={styles['toolbar-actions']}>
                                <Button
                                    size="sm"
                                    onClick={() => handleAssignBrokers(side)}
                                    disabled={assigning || (selAgg.size === 0 && selNri.size === 0)}
                                >
                                    {assigning ? 'Assigning…' : 'Assign Brokers'}
                                </Button>
                            </div>
                        </div>

                        {aggOrders.length > 0 && (
                            <div className={supportsNri ? styles['sub-section'] : ''}>
                                {supportsNri && (
                                    <div className={styles['sub-section-header']}>
                                        <div className={styles['sub-section-line']} />
                                        <span className={styles['badge-non-nri']}>Non-NRI — Aggregated</span>
                                        <div className={styles['sub-section-line']} />
                                    </div>
                                )}
                                {selectedSchemes.length > 0 && (
                                    <div className={styles['bulk-broker']}>
                                        <div className={styles['bulk-broker-head']}>
                                            <div className={styles['bulk-broker-heading']}>
                                                <span className={styles['bulk-broker-title']}>Bulk broker assignment</span>
                                                <span className={styles['bulk-broker-sub']}>
                                                    {selAgg.size} selected group{selAgg.size !== 1 ? 's' : ''} · save with Assign Brokers
                                                </span>
                                            </div>
                                            <Button size="sm" onClick={() => handleApplyBulkBroker(side)}>Apply to selected</Button>
                                        </div>
                                        {/* Brokers are scheme-specific, so each scheme in the selection gets its own pick */}
                                        <div className={styles['bulk-broker-grid']}>
                                            {selectedSchemes.map(scheme => {
                                                const groupCount = aggOrders.filter(o => o.scheme.id === scheme.id && selAgg.has(aggregatedKey(o))).length;
                                                return (
                                                    <label key={scheme.id} className={styles['bulk-broker-field']}>
                                                        <span className={styles['summary-chip-label']}>
                                                            {scheme.code} · {groupCount} group{groupCount !== 1 ? 's' : ''}
                                                        </span>
                                                        <select
                                                            className={styles['bulk-broker-select']}
                                                            value={getBulkBroker(side, scheme.id) ?? ''}
                                                            onChange={e => handleBulkBrokerChange(side, scheme.id, parseInt(e.target.value, 10))}
                                                        >
                                                            {sharedSchemeBrokers(scheme.id).length === 0 && <option value="">No brokers</option>}
                                                            {sharedSchemeBrokers(scheme.id).map(b => (
                                                                <option key={b.id} value={b.id}>
                                                                    {b.broker_code} — {b.broker_name}{b.is_default ? ' (Default)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                <TableWrapper>
                                    <table className={`${shared.table} ${styles.table}`}>
                                        <thead>
                                            <tr>
                                                <th className={styles['col-check']}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isAggAll} 
                                                        onChange={e => handleSelectAllAggregated(side, e.target.checked)} 
                                                    />
                                                </th>
                                                <th>Scheme</th>
                                                <th>{supportsNri ? 'Company' : 'Symbol'}</th>
                                                {renderExtraAggregatedHeaders?.()}
                                                <th className={styles.num}>{amountLabel}</th>
                                                <th>Broker</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggOrders.map(row => {
                                                const key = aggregatedKey(row);
                                                const schemeBrokers = brokersByScheme.get(`scheme_${row.scheme.id}`) ?? [];
                                                const pendingBrokerId = pendingAgg.get(key);
                                                const displayBrokerId = pendingBrokerId !== undefined ? (pendingBrokerId ?? '') : (row.broker?.id ?? '');
                                                const hasPending = pendingAgg.has(key);
                                                
                                                return (
                                                    <tr key={key}>
                                                        <td className={styles['col-check']}>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selAgg.has(key)} 
                                                                onChange={() => handleAggregatedSelect(key, side)} 
                                                            />
                                                        </td>
                                                        <td>{row.scheme.code}</td>
                                                        <td>{row.company.symbol}</td>
                                                        {renderExtraAggregatedCells?.(row)}
                                                        <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(row.total_amount, row.scheme.currency)}</td>
                                                        <td>
                                                            <div className={styles['broker-cell']}>
                                                                <div className={`${styles['broker-dot']} ${row.broker ? styles['broker-dot-assigned'] : styles['broker-dot-unassigned']}`} title={row.broker ? 'Assigned' : 'Not assigned'} />
                                                                <select
                                                                    className={`${styles['broker-select']} ${hasPending ? styles['broker-select-pending'] : ''}`}
                                                                    value={displayBrokerId}
                                                                    onChange={e => handleAggregatedBrokerChange(key, side, e.target.value ? parseInt(e.target.value) : null)}
                                                                >
                                                                    <option value="">No Broker</option>
                                                                    {schemeBrokers.map(b => (
                                                                        <option key={b.id} value={b.id}>
                                                                            {b.broker_code} — {b.broker_name}{b.is_default ? ' (Default)' : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </TableWrapper>
                            </div>
                        )}

                        {supportsNri && nriOrders.length > 0 && (
                            <div className={styles['sub-section']}>
                                <div className={styles['sub-section-header']}>
                                    <div className={styles['sub-section-line']} />
                                    <span className={styles['badge-nri']}>NRI — Individual</span>
                                    <div className={styles['sub-section-line']} />
                                </div>
                                <TableWrapper>
                                    <table className={`${shared.table} ${styles.table}`}>
                                        <thead>
                                            <tr>
                                                <th className={styles['col-check']}>
                                                    <input type="checkbox" checked={isNriAll} onChange={e => handleSelectAllNri(side, e.target.checked)} />
                                                </th>
                                                <th>Order Date</th>
                                                <th>Scheme</th>
                                                <th>Company</th>
                                                <th>Client Code</th>
                                                <th>Client Name</th>
                                                <th className={styles.num}>Quantity</th>
                                                <th className={styles.num}>{amountLabel}</th>
                                                <th>Broker</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nriOrders.map(order => {
                                                const brokerKey = `scheme_${order.scheme.id}_client_${order.client.id}`;
                                                const schemeBrokers = brokersByScheme.get(brokerKey) || [];
                                                const defaultBroker = getDefaultBroker(order);
                                                const pendingBrokerId = pendingNri.get(order.id);
                                                const hasPending = pendingNri.has(order.id);
                                                
                                                let displayBrokerId: number | string = '';
                                                if (pendingBrokerId !== undefined) {
                                                    displayBrokerId = pendingBrokerId ?? '';
                                                } else {
                                                    displayBrokerId = order.broker?.id ?? defaultBroker?.id ?? '';
                                                }

                                                return (
                                                    <tr key={order.id}>
                                                        <td className={styles['col-check']}>
                                                            <input type="checkbox" checked={selNri.has(order.id)} onChange={() => handleNriSelect(order.id, side)} />
                                                        </td>
                                                        <td>{formatDate(order.order_date)}</td>
                                                        <td>{order.scheme.code}</td>
                                                        <td>{order.company.symbol}</td>
                                                        <td>{order.client.code}</td>
                                                        <td>{order.client.name}</td>
                                                        <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.quantity, 0)}</td>
                                                        <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.amount, order.scheme.currency)}</td>
                                                        <td>
                                                            <div className={styles['broker-cell']}>
                                                                <div className={`${styles['broker-dot']} ${order.broker ? styles['broker-dot-assigned'] : styles['broker-dot-unassigned']}`} title={order.broker ? 'Assigned' : 'Not assigned'} />
                                                                <select
                                                                    className={`${styles['broker-select']} ${hasPending ? styles['broker-select-pending'] : ''}`}
                                                                    value={displayBrokerId}
                                                                    onChange={e => handleNriBrokerChange(order.id, side, e.target.value ? parseInt(e.target.value) : null)}
                                                                >
                                                                    <option value="">No Broker</option>
                                                                    {schemeBrokers.map(b => (
                                                                        <option key={b.id} value={b.id}>
                                                                            {b.broker_code} — {b.broker_name}{b.is_default ? ' (Default)' : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <OrderStatusPill stage={calculatedOrderStage(order.order_status)} label={order.order_status_display} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </TableWrapper>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    if (initialLoading) return <Placeholder kind="loading" />;

    return (
        <div>
            {(successMessage || error) && (
                <div className={styles.messages}>
                    {successMessage && <Note tone="success">{successMessage}</Note>}
                    {error && <Note tone="error">{error}</Note>}
                </div>
            )}

            {renderSchemeSection('buy')}
            {renderSchemeSection('sell')}

            {supportsClientWiseEdit && updateClientOrderQuantities && (
                <ClientWiseOrdersSection
                    titlePrefix={titlePrefix}
                    clientBuyOrders={clientBuyOrders}
                    clientSellOrders={clientSellOrders}
                    updateClientOrderQuantities={updateQuantitiesAndReload}
                />
            )}

            {pendingSubmit !== null && (
                <SubmitOrdersModal
                    side={pendingSubmit}
                    titlePrefix={titlePrefix}
                    emailRecipients={emailRecipients}
                    onEmailChange={setEmailRecipients}
                    onCancel={() => setPendingSubmit(null)}
                    onConfirm={confirmSubmit}
                />
            )}
        </div>
    );
};

export default BaseCalculatedOrders;
